import { Select, SelectItem } from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../../components/ResizableDivider';
import { usePipelineRuns } from '../hooks/usePipelineRuns';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type { LinkedRepoOption, SourceSystemPanelState } from '../types/source-system';
import { PipelineRunsViewer } from './PipelineRunsViewer';
import { PipelineSelector } from './PipelineSelector';

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 400;
const DEFAULT_SIDEBAR_WIDTH = 260;

interface PipelineBrowserProps {
  projectUuid: string;
  state: SourceSystemPanelState | null;
  /** Bumped whenever a sync completes - triggers run list refresh. */
  refreshKey: string | null;
}

/**
 * Pipeline browser - left-right split layout with repo-scoped filtering.
 *
 * Left sidebar: repository selector dropdown + filtered pipeline list.
 * Right pane:   run history + definition metadata (PipelineRunsViewer).
 */
export function PipelineBrowser({ projectUuid, state, refreshKey }: PipelineBrowserProps) {
  const resources = state?.resources ?? [];

  // Linked repos for the filter dropdown
  const [linkedRepos, setLinkedRepos] = React.useState<LinkedRepoOption[]>([]);
  const [selectedRepoId, setSelectedRepoId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!projectUuid) return;
    sourceSystemApi
      .getPipelineLinkedRepos(projectUuid)
      .then(({ repos }) => {
        setLinkedRepos(repos);
        if (repos.length > 0 && !selectedRepoId) {
          setSelectedRepoId(repos[0]?.repoProviderResourceId ?? null);
        }
      })
      .catch(() => {
        // Non-fatal - browser degrades to showing all pipelines
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid]);

  // Filter pipeline resources by selected repo
  const filteredResources = React.useMemo(() => {
    if (!selectedRepoId) return resources;
    return resources.filter((r) => r.linkedRepoProviderIds.includes(selectedRepoId));
  }, [resources, selectedRepoId]);

  // Selected pipeline UUID
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(
    () => filteredResources[0]?.resourceUuid ?? null
  );

  // Auto-select first resource when filtered list changes (repo switch or initial load)
  React.useEffect(() => {
    const first = filteredResources[0]?.resourceUuid ?? null;
    setSelectedUuid((prev) => {
      const stillVisible = prev && filteredResources.some((r) => r.resourceUuid === prev);
      return stillVisible ? prev : first;
    });
  }, [filteredResources]);

  const handleRepoChange = React.useCallback((repoProviderResourceId: string) => {
    setSelectedRepoId(repoProviderResourceId);
    setSelectedUuid(null); // reset - effect above will pick the new first entry
  }, []);

  const { data, loading, error, refresh } = usePipelineRuns(
    projectUuid,
    selectedUuid,
    20,
    refreshKey
  );

  const [sidebarWidth, setSidebarWidth] = React.useState(DEFAULT_SIDEBAR_WIDTH);

  const handleResize = React.useCallback((delta: number) => {
    setSidebarWidth((w) => Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, w + delta)));
  }, []);

  const selectedResource = filteredResources.find((r) => r.resourceUuid === selectedUuid) ?? null;

  return (
    <section className="pl-browser" aria-label="Pipeline browser">
      {/* ── Main split ──────────────────────────────────────────────────── */}
      <div className="pl-browser__layout">
        {/* Left sidebar */}
        <aside className="pl-browser__sidebar" style={{ width: `${sidebarWidth}px` }}>
          <div className="pl-browser__sidebar-header">
            <h2 className="ss-section__title">Repositories</h2>
            {linkedRepos.length > 0 && (
              <div className="pl-browser__repo-selector">
                <Select
                  value={selectedRepoId ?? ''}
                  onValueChange={handleRepoChange}
                  placeholder="Select repo"
                  size="sm"
                >
                  {linkedRepos.map((r) => (
                    <SelectItem key={r.repoProviderResourceId} value={r.repoProviderResourceId}>
                      {r.repoName ?? r.repoProviderResourceId}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <div className="pl-browser__sidebar-body">
            <PipelineSelector
              resources={filteredResources}
              selectedResourceUuid={selectedUuid}
              onSelect={setSelectedUuid}
            />
          </div>
        </aside>

        <ResizableDivider
          direction="vertical"
          onResize={handleResize}
          className="ss-browser__divider"
          ariaLabel="Resize pipeline list"
        />

        {/* Right content pane */}
        <div className="pl-browser__viewer">
          {/* Empty header - provides the border-bottom that bridges the divider tick */}
          <div className="pl-browser__viewer-header" />
          <PipelineRunsViewer
            resource={selectedResource}
            data={data}
            loading={loading}
            error={error}
            onRetry={refresh}
          />
        </div>
      </div>
    </section>
  );
}
